import React, {
  useMemo,
  useState,
} from "react";

import {
  FaArrowDown,
  FaArrowUp,
  FaEdit,
  FaEye,
  FaEyeSlash,
  FaImage,
  FaPhotoVideo,
  FaTrashAlt,
} from "react-icons/fa";

import TutorialMediaList from "./TutorialMediaList";

import {
  resolveAdminTutorialMediaUrl,
} from "../utils/tutorialAdminMediaUtils";

function truncate(
  value = "",
  max = 300
) {
  const text =
    String(value || "");

  if (text.length <= max) {
    return text;
  }

  return `${text.slice(
    0,
    max - 1
  )}…`;
}

function sortByOrder(items = []) {
  return [...items].sort(
    (a, b) =>
      Number(a.ordre || 0) -
      Number(b.ordre || 0)
  );
}

export default function TutorialStepCard({
  step,
  index,
  total,
  busy,

  onEdit,
  onDelete,
  onToggleActive,
  onMoveUp,
  onMoveDown,

  onCreateMedia,
  onEditMedia,
  onDeleteMedia,
  onToggleMediaActive,
}) {
  const [showMedias, setShowMedias] =
    useState(false);

  const medias = useMemo(
    () =>
      sortByOrder(
        step.medias || []
      ),
    [step.medias]
  );

  const mainImage =
    medias.find(
      (media) =>
        media.type_media ===
          "image" &&
        media.is_active !== false
    ) || null;

  const active =
    step.is_active !== false;

  return (
    <article
      className={`rounded-[22px] border p-4 transition sm:p-5 ${
        active
          ? "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
          : "border-slate-200 bg-slate-100 opacity-70 dark:border-slate-800 dark:bg-slate-900/50"
      }`}
    >
      <div className="flex flex-col gap-4 xl:flex-row">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-violet-100 font-bold text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
          {index + 1}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-semibold text-slate-900 dark:text-slate-50">
              {step.titre}
            </h4>

            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              ordre {step.ordre}
            </span>

            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                active
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                  : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              {active
                ? "Active"
                : "Inactive"}
            </span>
          </div>

          <div className="mt-1 text-xs text-slate-400">
            {step.slug}
          </div>

          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {truncate(step.contenu)}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <FaImage />
              {medias.length} média
              {medias.length > 1
                ? "s"
                : ""}
            </span>

            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {step.format_contenu ||
                "texte"}
            </span>
          </div>
        </div>

        {mainImage ? (
          <StepImagePreview
            media={mainImage}
            stepTitle={step.titre}
          />
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4 dark:border-slate-800">
        <button
          type="button"
          disabled={
            busy || index === 0
          }
          onClick={onMoveUp}
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-100 disabled:opacity-30 dark:border-slate-700 dark:hover:bg-slate-800"
          title="Monter"
        >
          <FaArrowUp />
        </button>

        <button
          type="button"
          disabled={
            busy ||
            index === total - 1
          }
          onClick={onMoveDown}
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-100 disabled:opacity-30 dark:border-slate-700 dark:hover:bg-slate-800"
          title="Descendre"
        >
          <FaArrowDown />
        </button>

        <button
          type="button"
          disabled={busy}
          onClick={() =>
            setShowMedias(
              (current) => !current
            )
          }
          className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition disabled:opacity-50 ${
            showMedias
              ? "border-violet-300 bg-violet-100 text-violet-700 dark:border-violet-800 dark:bg-violet-500/15 dark:text-violet-300"
              : "border-slate-200 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          }`}
        >
          <FaPhotoVideo />

          {showMedias
            ? "Masquer les médias"
            : `Gérer les médias (${medias.length})`}
        </button>

        <div className="ml-auto flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onToggleActive}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {active ? (
              <FaEyeSlash />
            ) : (
              <FaEye />
            )}

            {active
              ? "Désactiver"
              : "Activer"}
          </button>

          <button
            type="button"
            disabled={busy}
            onClick={onEdit}
            className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-medium text-violet-700 transition hover:bg-violet-100 disabled:opacity-50 dark:border-violet-900/50 dark:bg-violet-500/10 dark:text-violet-300"
          >
            <FaEdit />
            Modifier
          </button>

          <button
            type="button"
            disabled={busy}
            onClick={onDelete}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-700 transition hover:bg-rose-100 disabled:opacity-50 dark:border-rose-900/50 dark:bg-rose-500/10 dark:text-rose-300"
            title="Supprimer"
          >
            <FaTrashAlt />
          </button>
        </div>
      </div>

      {showMedias ? (
        <TutorialMediaList
          medias={medias}
          busy={busy}
          onCreate={
            onCreateMedia
          }
          onEdit={onEditMedia}
          onDelete={onDeleteMedia}
          onToggleActive={
            onToggleMediaActive
          }
        />
      ) : null}
    </article>
  );
}

function StepImagePreview({
  media,
  stepTitle,
}) {
  const [hasError, setHasError] =
    useState(false);

  if (hasError) {
    return (
      <div className="flex h-28 w-full shrink-0 items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-800 xl:w-44">
        <FaImage />
      </div>
    );
  }

  return (
    <div className="flex h-28 w-full shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-800 xl:w-44">
      <img
        src={resolveAdminTutorialMediaUrl(
          media.miniature_url ||
            media.url
        )}
        alt={
          media.alt_text ||
          stepTitle
        }
        onError={() =>
          setHasError(true)
        }
        className="h-full w-full object-contain"
      />
    </div>
  );
}